/**
 * Inventory Management Component
 * Manage materials inventory with reporting and advanced settings
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  Grid,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  Assessment as AssessmentIcon,
  Calculate as CalculateIcon,
  LocalShipping as LocalShippingIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import type { Material, InventoryReport, BuyList, ProduceList, TransitOrder } from '../types/madebb.types';
import { authFetch } from '../utils/authFetch'; // kept for any non-service call residuals
import * as inventoryService from '../services/inventoryService';
import { getStockStatus, organizeHierarchy, formatCurrency, formatDate, formatStock } from '../utils/inventoryHelpers';
// OS-INV-14: PIN admin panel (lazy-imported so it doesn't bloat the Made.bb bundle)
import { OsPinAdmin } from '../../os/components/OsPinAdmin';

export const Inventory: React.FC = () => {
  const navigate = useNavigate();
  // OS-INV-04: tenant-aware API base + route base. Defaults to Made.bb when no
  // TenantProvider wraps this component, so existing Made.bb behavior is unchanged.
  const { tenant, apiBase, routeBase } = useTenant();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [buyListDialogOpen, setBuyListDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [transitInventoryDialogOpen, setTransitInventoryDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [createVariantDialogOpen, setCreateVariantDialogOpen] = useState(false);
  const [assignParentDialogOpen, setAssignParentDialogOpen] = useState(false);
  const [generateVariantsDialogOpen, setGenerateVariantsDialogOpen] = useState(false);

  // Data states
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [materialToDelete, setMaterialToDelete] = useState<Material | null>(null);
  const [newMaterial, setNewMaterial] = useState<Material | null>(null);
  const [inventoryReport, setInventoryReport] = useState<InventoryReport | null>(null);
  const [buyList, setBuyList] = useState<BuyList | null>(null);
  const [materialToOrder, setMaterialToOrder] = useState<Material | null>(null);
  const [orderForm, setOrderForm] = useState({
    quantity: 0,
    costPerUnit: 0,
    expectedDeliveryDate: '',
    supplier: '',
    notes: ''
  });
  const [transitOrders, setTransitOrders] = useState<TransitOrder[]>([]);
  const [orderToReceive, setOrderToReceive] = useState<TransitOrder | null>(null);
  const [receiveForm, setReceiveForm] = useState({
    additionalFees: 0,
    notes: ''
  });
  const [variantForm, setVariantForm] = useState({
    variantValue: '',
    id: ''
  });
  const [bomUsageItems, setBomUsageItems] = useState<Array<{ id: string; name: string; type: string }>>([]);
  const [selectedItemsToReplicate, setSelectedItemsToReplicate] = useState<string[]>([]);
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [expandedMasters, setExpandedMasters] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'status' | 'name' | 'buyOrProduce' | 'lastRestocked'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // MADE-267: shop_visible filter
  const [shopVisibleFilter, setShopVisibleFilter] = useState<boolean | null>(null);
  const [variantPreview, setVariantPreview] = useState<any>(null);
  const [generatingVariants, setGeneratingVariants] = useState(false);
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [cascadePreview, setCascadePreview] = useState<any>(null);
  const [cascading, setCascading] = useState(false);
  // MADE-270: which product IDs are checked for push
  const [selectedCascadeIds, setSelectedCascadeIds] = useState<Set<string>>(new Set());
  const [newlyCreatedVariantId, setNewlyCreatedVariantId] = useState<string | null>(null);
  // MADE-269: BOM colour suggestion banner state
  interface BomColorSuggestion {
    materialId: string;
    suggestedColor: string | null;
    ambiguousColors: string[] | null;
    currentColor: string | null;
  }
  const [bomColorSuggestion, setBomColorSuggestion] = useState<BomColorSuggestion | null>(null);
  const [bomColorSuggestionChoice, setBomColorSuggestionChoice] = useState<string>('');

  useEffect(() => {
    loadMaterials();
  }, [shopVisibleFilter]); // re-fetch when filter changes

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const materials = await inventoryService.loadMaterials(apiBase, shopVisibleFilter);
      setMaterials(materials);
    } catch (err: any) {
      setError(err.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (material: Material) => {
    setSelectedMaterial({ ...material });
    setEditDialogOpen(true);
  };

  const handleAddClick = () => {
    setNewMaterial({
      id: '',
      type: 'component',
      name: '',
      currentStock: 0,
      minStock: 0,
      reorderQuantity: 0,
      unit: 'pieces',
      lastRestocked: new Date().toISOString(),
      active: true,
      buyOrProduce: 'buy',
      billOfMaterials: []
    });
    setAddDialogOpen(true);
  };

  const handleSaveNewMaterial = async () => {
    if (!newMaterial) return;

    if (!newMaterial.id || !newMaterial.name) {
      setError('Material ID and Name are required');
      setTimeout(() => setError(null), 5000);
      return;
    }

    try {
      await inventoryService.createMaterial(newMaterial, apiBase);
      setSuccessMessage('Material created successfully');
      setAddDialogOpen(false);
      setNewMaterial(null);
      loadMaterials();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create material');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSaveMaterial = async () => {
    if (!selectedMaterial) return;

    try {
      const { shop_visible, ...materialToUpdate } = selectedMaterial;
      const result = await inventoryService.updateMaterial(materialToUpdate.id, materialToUpdate, apiBase);
      loadMaterials();

      // MADE-269: check for BOM colour suggestion
      if (materialToUpdate.billOfMaterials !== undefined &&
          (result.suggested_color || result.ambiguous_colors)) {
        setBomColorSuggestion({
          materialId: materialToUpdate.id,
          suggestedColor: result.suggested_color ?? null,
          ambiguousColors: result.ambiguous_colors ?? null,
          currentColor: materialToUpdate.color || null,
        });
        setBomColorSuggestionChoice(result.suggested_color ?? '');
        // Don't close dialog — banner shown inside it
        setSuccessMessage('Material saved. Review the colour suggestion below.');
      } else {
        setSuccessMessage('Material updated successfully');
        setEditDialogOpen(false);
      }
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update material');
      setTimeout(() => setError(null), 5000);
    }
  };

  // MADE-269: apply the BOM-suggested colour to the material
  const handleApplyBomColor = async (colorToApply: string) => {
    if (!bomColorSuggestion) return;
    try {
      await inventoryService.updateMaterial(
        bomColorSuggestion.materialId,
        { id: bomColorSuggestion.materialId, color: colorToApply, color_source: 'bom_auto' } as any,
        apiBase
      );
      setBomColorSuggestion(null);
      setEditDialogOpen(false);
      loadMaterials();
      setSuccessMessage(`Colour set to "${colorToApply}" (auto from BOM)`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to apply colour');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleShopVisibleToggle = async (materialId: string, newShopVisible: boolean) => {
    try {
      await inventoryService.updateMaterialShopVisible(materialId, newShopVisible, apiBase);
      setMaterials(prev => prev.map(m => m.id === materialId ? { ...m, shop_visible: newShopVisible } : m));
      setSuccessMessage('Shop visibility updated');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update visibility');
      // Revert the change in the UI on error
      setMaterials(prev => prev.map(m => m.id === materialId ? { ...m, shop_visible: !newShopVisible } : m));
      setTimeout(() => setError(null), 5000);
    }
  };


  const handleDeleteClick = (material: Material) => {
    setMaterialToDelete(material);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!materialToDelete) return;

    try {
      await inventoryService.deleteMaterial(materialToDelete.id, apiBase);
      setSuccessMessage(`Material "${materialToDelete.name}" deleted successfully`);
      setDeleteDialogOpen(false);
      setMaterialToDelete(null);
      loadMaterials();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete material');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleGenerateReport = async () => {
    try {
      const report = await inventoryService.getInventoryReport(apiBase);
      setInventoryReport(report);
      setReportDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate report');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleGenerateBuyList = async () => {
    try {
      const buyList = await inventoryService.getBuyList(apiBase);
      setBuyList(buyList);
      setBuyListDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate buy list');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleOrderClick = (material: Material) => {
    const unitCost = material.unit === 'yards' ? material.costPerYard : material.costPerUnit;
    setMaterialToOrder(material);
    setOrderForm({
      quantity: material.reorderQuantity,
      costPerUnit: unitCost || 0,
      expectedDeliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to 7 days from now
      supplier: material.supplier || '',
      notes: ''
    });
    setOrderDialogOpen(true);
  };

  const handleSubmitOrder = async () => {
    if (!materialToOrder) return;

    try {
      await inventoryService.placeTransitOrder({
        materialId: materialToOrder.id,
        quantityOrdered: orderForm.quantity,
        costPerUnit: orderForm.costPerUnit,
        expectedDeliveryDate: orderForm.expectedDeliveryDate,
        supplier: orderForm.supplier || null,
        notes: orderForm.notes || null,
      }, apiBase);
      setSuccessMessage(`✅ Order placed for ${materialToOrder.name}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setOrderDialogOpen(false);
      setMaterialToOrder(null);
      if (transitInventoryDialogOpen) {
        loadTransitOrders();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to place order');
      setTimeout(() => setError(null), 5000);
    }
  };

  const loadTransitOrders = async () => {
    try {
      const orders = await inventoryService.getTransitOrders(apiBase);
      setTransitOrders(orders);
    } catch (err: any) {
      setError(err.message || 'Failed to load transit orders');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleViewTransitInventory = async () => {
    await loadTransitOrders();
    setTransitInventoryDialogOpen(true);
  };

  const handleReceiveClick = (order: TransitOrder) => {
    setOrderToReceive(order);
    setReceiveForm({
      additionalFees: 0,
      notes: ''
    });
    setReceiveDialogOpen(true);
  };

  const handleSubmitReceive = async () => {
    if (!orderToReceive) return;

    try {
      const result = await inventoryService.receiveTransitOrder(orderToReceive.transitId, {
        additionalFees: receiveForm.additionalFees || 0,
        notes: receiveForm.notes || null,
      }, apiBase);
      setSuccessMessage(`✅ Received ${orderToReceive.materialName} - New stock: ${result.newStock.toFixed(2)}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      setReceiveDialogOpen(false);
      setOrderToReceive(null);
      await loadTransitOrders();
      await loadMaterials();
    } catch (err: any) {
      setError(err.message || 'Failed to receive order');
      setTimeout(() => setError(null), 5000);
    }
  };

  // Handle creating a color variant
  const handleCreateVariant = async (material: Material) => {
    // Check if this material has variants (can add siblings) or is regular (becomes parent)
    const parentId = material.parentMaterialId || material.id;

    // Find parent material (or use this one if it's not a variant)
    const parent = materials.find(m => m.id === parentId);
    if (!parent) {
      setError('Parent material not found');
      return;
    }

    // Generate suggested ID
    const suggestedId = `${parent.id}-VARIANT-${Date.now().toString().slice(-4)}`;

    setVariantForm({
      variantValue: '',
      id: suggestedId
    });

    // Check BOM usage
    try {
      const result = await inventoryService.getBomUsage(material.id, apiBase);
      setBomUsageItems(result.usedIn || []);
      setSelectedItemsToReplicate(result.usedIn?.map((item: any) => item.id) || []);
    } catch (err) {
      console.error('Failed to check BOM usage:', err);
      setBomUsageItems([]);
      setSelectedItemsToReplicate([]);
    }

    setSelectedMaterial(material);
    setCreateVariantDialogOpen(true);
  };

  const handleSubmitVariant = async () => {
    if (!selectedMaterial || !variantForm.variantValue || !variantForm.id) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Determine parent ID
      const parentId = selectedMaterial.parentMaterialId || selectedMaterial.id;

      // Get parent material to copy properties
      const parent = materials.find(m => m.id === parentId) || selectedMaterial;

      // Create new variant
      const newVariant = {
        id: variantForm.id.toUpperCase(),
        name: `${parent.name} - ${variantForm.variantValue}`,
        type: parent.type,
        color: variantForm.variantValue, // Populate color field with variant value
        unit: parent.unit,
        parentMaterialId: parentId,
        isMaster: false,
        variantType: 'color',
        variantValue: variantForm.variantValue,
        currentStock: 0,
        minStock: parent.minStock,
        reorderQuantity: parent.reorderQuantity,
        costPerYard: parent.costPerYard,
        costPerUnit: parent.costPerUnit,
        supplier: parent.supplier,
        buyOrProduce: parent.buyOrProduce,
        active: true,
        notes: `Color variant: ${variantForm.variantValue}`
      };

      await inventoryService.createVariant(newVariant as any, apiBase);
      setSuccessMessage(`✅ Created variant: ${newVariant.name}`);

      // If items selected for replication, replicate them
      if (selectedItemsToReplicate.length > 0) {
        const replicateData = await inventoryService.replicateWithVariant({
          sourceMaterialIds: selectedItemsToReplicate,
          oldVariantId: selectedMaterial.id,
          newVariantId: variantForm.id.toUpperCase(),
        }, apiBase);
        if (replicateData.created.length > 0) {
          setSuccessMessage(`✅ Created variant and replicated ${replicateData.created.length} item(s)`);
        }
      }

      // MADE-270: dry-run preview
      try {
        const cascadeData = await inventoryService.cascadeToManufactured(
          variantForm.id.toUpperCase(),
          { dryRun: true },
          apiBase
        );
        const toCreate = cascadeData.would_create ?? cascadeData.preview ?? [];
        if (cascadeData.success && toCreate.length > 0) {
          setNewlyCreatedVariantId(variantForm.id.toUpperCase());
          setCascadePreview(cascadeData);
          // Pre-check all would-create items
          const ids = new Set<string>(toCreate.map((item: any) => item.masterId ?? item.product_id ?? ''));
          setSelectedCascadeIds(ids);
          setCascadeDialogOpen(true);
          setCreateVariantDialogOpen(false);
          return;
        }
      } catch (err) {
        console.error('Failed to check cascade opportunities:', err);
      }

      // Reload and close
      await loadMaterials();
      setCreateVariantDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to create variant');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleConvertToMaster = async (material: Material) => {
    if (!material) return;

    const confirmed = window.confirm(
      `Convert "${material.name}" to a master material?\n\n` +
      `This will:\n` +
      `- Set this material as a master (for grouping variants)\n` +
      `- Clear the color field\n` +
      `- Set current stock to 0\n\n` +
      `Current stock: ${formatStock(material.currentStock)} ${material.unit}\n` +
      `Are you sure you want to continue?`
    );

    if (!confirmed) return;

    try {
      const updatedMaterial = {
        ...material,
        isMaster: true,
        color: '',
        currentStock: 0,
        variantType: 'color',
        notes: (material.notes || '') + '\n[Converted to master material]'
      };

      await inventoryService.updateMaterial(material.id, updatedMaterial, apiBase);
      setSuccessMessage(`✅ Converted "${material.name}" to master material`);
      await loadMaterials();
      setEditDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to convert to master');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAssignParent = async () => {
    if (!selectedMaterial || !selectedParentId) {
      setError('Please select a parent material');
      return;
    }

    // Prevent assigning to self
    if (selectedParentId === selectedMaterial.id) {
      setError('Cannot assign a material as its own parent');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Prevent assigning to a non-master
    const parentMaterial = materials.find(m => m.id === selectedParentId);
    if (parentMaterial && !parentMaterial.isMaster) {
      setError('Parent material must be a master material');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      // Suggest a variant value based on the color field
      const variantValue = selectedMaterial.color || 'Default';

      const updatedMaterial = {
        ...selectedMaterial,
        parentMaterialId: selectedParentId,
        isMaster: false,
        variantType: 'color',
        variantValue: variantValue,
        name: `${parentMaterial?.name} - ${variantValue}`
      };

      await inventoryService.assignParent(selectedMaterial.id, updatedMaterial, apiBase);
      setSuccessMessage(`✅ Assigned "${selectedMaterial.name}" to parent "${parentMaterial?.name}"`);
      await loadMaterials();
      setAssignParentDialogOpen(false);
      setEditDialogOpen(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to assign parent');
      setTimeout(() => setError(null), 5000);
    }
  };

  // getStockStatus is imported from inventoryHelpers — adds icons for UI use
  const getStockStatusWithIcon = (material: Material) => {
    const status = getStockStatus(material);
    const icon = status.color === 'success' ? <CheckCircleIcon /> : <WarningIcon />;
    return { ...status, icon };
  };

  const toggleMasterExpansion = (masterId: string) => {
    setExpandedMasters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(masterId)) {
        newSet.delete(masterId);
      } else {
        newSet.add(masterId);
      }
      return newSet;
    });
  };

  // Generate variants from BOM
  const handleGenerateVariantsClick = async () => {
    if (!selectedMaterial) return;

    try {
      setGeneratingVariants(true);

      const data = await inventoryService.generateVariants(selectedMaterial.id, false, apiBase);

      if (data.variantCount === 0) {
        setError('No variants can be generated. This material needs a BOM with materials that have color variants.');
        setTimeout(() => setError(null), 5000);
        return;
      }

      setVariantPreview(data);
      setGenerateVariantsDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate variants');
      setTimeout(() => setError(null), 5000);
    } finally {
      setGeneratingVariants(false);
    }
  };

  const handleConfirmGenerateVariants = async () => {
    if (!selectedMaterial) return;

    try {
      setGeneratingVariants(true);

      const data = await inventoryService.generateVariants(selectedMaterial.id, true, apiBase);
      setSuccessMessage(`✅ Created ${data.variantCount} variant${data.variantCount !== 1 ? 's' : ''} successfully!`);
      setTimeout(() => setSuccessMessage(null), 5000);

      setGenerateVariantsDialogOpen(false);
      setVariantPreview(null);

      // Reload materials to show new variants
      await loadMaterials();
    } catch (err: any) {
      setError(err.message || 'Failed to create variants');
      setTimeout(() => setError(null), 5000);
    } finally {
      setGeneratingVariants(false);
    }
  };

  const handleConfirmCascade = async () => {
    if (!newlyCreatedVariantId) return;

    try {
      setCascading(true);

      // MADE-270: pass only selected masterIds (targetMaterialIds)
      const targetIds = [...selectedCascadeIds].filter(Boolean);
      const data = await inventoryService.cascadeToManufactured(
        newlyCreatedVariantId,
        { dryRun: false, confirm: true, targetMaterialIds: targetIds.length > 0 ? targetIds : undefined },
        apiBase
      );
      const count = data.created_count ?? data.cascadeCount ?? 0;
      setSuccessMessage(`✅ Created ${count} variant${count !== 1 ? 's' : ''} (shop_visible=false — publish when ready)`);
      setTimeout(() => setSuccessMessage(null), 5000);

      setCascadeDialogOpen(false);
      setCascadePreview(null);
      setNewlyCreatedVariantId(null);
      setSelectedCascadeIds(new Set());

      // Reload materials to show new variants
      await loadMaterials();
    } catch (err: any) {
      setError(err.message || 'Failed to create cascade variants');
      setTimeout(() => setError(null), 5000);
    } finally {
      setCascading(false);
    }
  };

  // Organize materials into hierarchical structure with rollup
  // organizeHierarchy is imported from inventoryHelpers — bind component state as args
  const getHierarchy = () => organizeHierarchy({ materials, searchTerm, sortBy, sortDirection });

  const handleSort = (column: 'status' | 'name' | 'buyOrProduce' | 'lastRestocked') => {
    if (sortBy === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  // formatCurrency and formatDate are imported from inventoryHelpers

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header is now in MadebbLayout */}

      {/* Error/Success Messages */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Action Buttons */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              Add New Item
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AssessmentIcon />}
              onClick={handleGenerateReport}
            >
              Financial Report
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={handleGenerateBuyList}
            >
              Buy List
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="info"
              startIcon={<LocalShippingIcon />}
              onClick={handleViewTransitInventory}
            >
              Transit Inventory
            </Button>
          </Grid>
          {/* OS-INV-04: Production is Made.bb-only. Offshore Sails has no
              manufacturing module, so this action is hidden for that tenant. */}
          {tenant !== 'offshore' && (
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AssessmentIcon />}
                onClick={() => navigate(`${routeBase}/manufacturing`)}
              >
                Production
              </Button>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<PrintIcon />}
              onClick={() => window.open(`${routeBase}/count-sheet`, '_blank')}
            >
              Print Count Sheet
            </Button>
          </Grid>
          {/* OS-INV-13: Labels link (offshore only) */}
          {tenant === 'offshore' && (
            <Grid item>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => navigate(`${routeBase}/labels`)}
              >
                Labels
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* OS-INV-14: PIN Admin panel (offshore only — staff manages device PINs here) */}
      {tenant === 'offshore' && <OsPinAdmin />}

      {/* Search/Filter — MADE-267: shop_visible checkbox added */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: 1, minWidth: 240 }}
            variant="outlined"
            placeholder="Search by name, type, color, supplier, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                  🔍
                </Box>
              )
            }}
          />
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            onClick={() => setShopVisibleFilter(v => v === true ? null : true)}
            data-testid="shop-visible-filter-wrapper"
          >
            <input
              type="checkbox"
              id="shop-visible-filter"
              checked={shopVisibleFilter === true}
              onChange={e => setShopVisibleFilter(e.target.checked ? true : null)}
              data-testid="shop-visible-filter-checkbox"
              style={{ cursor: 'pointer', width: 16, height: 16 }}
            />
            <label htmlFor="shop-visible-filter" style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
              Shop visible only
            </label>
          </Box>
        </Box>
      </Paper>

      {/* Inventory Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('status')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Status
                  {sortBy === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('name')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Name
                  {sortBy === 'name' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell>Color</TableCell>
              <TableCell align="right">Current Stock</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('buyOrProduce')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Buy/Produce
                  {sortBy === 'buyOrProduce' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('lastRestocked')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Last Restocked
                  {sortBy === 'lastRestocked' && (
                    sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getHierarchy().map((item) => {
              const { material, variants } = item;
              const status = getStockStatusWithIcon(material);
              const unitPrice = material.unit === 'yards' ? material.costPerYard : material.costPerUnit;
              const isMaster = material.isMaster;
              const isExpanded = expandedMasters.has(material.id);
              const hasVariants = variants && variants.length > 0;

              return (
                <React.Fragment key={material.id}>
                  {/* Master or Standalone Material Row */}
                  <TableRow
                    sx={{
                      backgroundColor: material.active ? 'inherit' : 'rgba(0,0,0,0.05)',
                      opacity: material.active ? 1 : 0.6
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                        icon={status.icon}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Expand/Collapse Icon for Masters with Variants */}
                        {isMaster && hasVariants && (
                          <IconButton
                            size="small"
                            onClick={() => toggleMasterExpansion(material.id)}
                            sx={{ p: 0 }}
                          >
                            {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                          </IconButton>
                        )}
                        {/* Spacer for alignment if no expand icon */}
                        {(!isMaster || !hasVariants) && <Box sx={{ width: 24 }} />}

                        <Typography fontWeight={material.active ? 'bold' : 'normal'}>
                          {material.name}
                        </Typography>

                        {isMaster && (
                          <Chip
                            label={`MASTER${hasVariants ? ` (${variants.length})` : ''}`}
                            size="small"
                            color="info"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{material.color || '-'}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={isMaster && hasVariants ? 'bold' : 'normal'}>
                        {formatStock(material.currentStock)} {material.unit}
                      </Typography>
                      {isMaster && hasVariants && (
                        <Typography variant="caption" color="text.secondary">
                          (rollup)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(unitPrice || 0)}/{material.unit}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={material.buyOrProduce === 'buy' ? 'Buy' : 'Produce'}
                        color={material.buyOrProduce === 'buy' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(material.lastRestocked)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditClick(material)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(material)}
                          title="Delete"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* Variant Rows (only show if master is expanded) */}
                  {isMaster && hasVariants && isExpanded && variants.map((variant) => {
                    const variantStatus = getStockStatusWithIcon(variant);
                    const variantUnitPrice = variant.unit === 'yards' ? variant.costPerYard : variant.costPerUnit;

                    return (
                      <TableRow
                        key={variant.id}
                        sx={{
                          backgroundColor: variant.active ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.07)',
                          opacity: variant.active ? 1 : 0.6,
                          borderLeft: '3px solid',
                          borderLeftColor: 'secondary.main'
                        }}
                      >
                        <TableCell>
                          <Chip
                            label={variantStatus.label}
                            color={variantStatus.color}
                            size="small"
                            icon={variantStatus.icon}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5 }}>
                            <Typography variant="body2" fontWeight={variant.active ? 'normal' : 'normal'}>
                              ↳ {variant.variantValue || variant.color || 'Variant'}
                            </Typography>
                            <Chip
                              label="VARIANT"
                              size="small"
                              color="secondary"
                              sx={{ fontSize: '0.6rem', height: '18px' }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{variant.color || '-'}</TableCell>
                        <TableCell align="right">
                          {formatStock(variant.currentStock)} {variant.unit}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(variantUnitPrice || 0)}/{variant.unit}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={variant.buyOrProduce === 'buy' ? 'Buy' : 'Produce'}
                            color={variant.buyOrProduce === 'buy' ? 'primary' : 'secondary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(variant.lastRestocked)}</TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditClick(variant)}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(variant)}
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Material Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Material: {selectedMaterial?.name}</DialogTitle>
        <DialogContent>
          {selectedMaterial && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Item Code (BOM Short Name)"
                  fullWidth
                  value={selectedMaterial.id}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      id: e.target.value.toUpperCase()
                    })
                  }
                  helperText="Short code used in Bill of Materials (e.g., SS-SCR, FAB-001)"
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  fullWidth
                  value={selectedMaterial.name}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      name: e.target.value
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={selectedMaterial.type}
                    label="Type"
                    onChange={(e) =>
                      setSelectedMaterial({
                        ...selectedMaterial,
                        type: e.target.value as 'fabric' | 'hardware' | 'component' | 'filament' | 'material'
                      })
                    }
                  >
                    <MenuItem value="fabric">Fabric</MenuItem>
                    <MenuItem value="hardware">Hardware</MenuItem>
                    <MenuItem value="component">Component</MenuItem>
                    <MenuItem value="filament">Filament</MenuItem>
                    <MenuItem value="material">Material</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Color (Optional)"
                  fullWidth
                  value={selectedMaterial.color || ''}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      color: e.target.value,
                      // MADE-269: manual edit clears auto badge
                      color_source: 'manual',
                    } as any)
                  }
                  disabled={selectedMaterial.isMaster}
                  helperText={
                    selectedMaterial.isMaster
                      ? "Master materials don't have colors - set on variants"
                      : (selectedMaterial as any).color_source === 'bom_auto'
                        ? '🤖 auto — inferred from BOM'
                        : ''
                  }
                  data-testid="color-field"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedMaterial.shop_visible || false}
                      onChange={(e) => {
                        const newShopVisible = e.target.checked;
                        setSelectedMaterial({
                          ...selectedMaterial,
                          shop_visible: newShopVisible,
                        });
                        handleShopVisibleToggle(selectedMaterial.id, newShopVisible);
                      }}
                      name="shop_visible"
                      color="primary"
                    />
                  }
                  label="Visible in Shop"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Stock Management
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Current Stock"
                  type="number"
                  fullWidth
                  value={selectedMaterial.currentStock}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      currentStock: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: selectedMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: selectedMaterial.unit
                  }}
                  disabled={selectedMaterial.isMaster}
                  helperText={selectedMaterial.isMaster ? "Stock is automatically calculated from variants" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Stock Level"
                  type="number"
                  fullWidth
                  value={selectedMaterial.minStock}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      minStock: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: selectedMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: selectedMaterial.unit
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Reorder Quantity"
                  type="number"
                  fullWidth
                  value={selectedMaterial.reorderQuantity}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      reorderQuantity: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: selectedMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: selectedMaterial.unit
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Pricing & Sourcing
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={`Cost per ${selectedMaterial.unit === 'yards' ? 'Yard' : 'Unit'}`}
                  type="number"
                  fullWidth
                  value={
                    selectedMaterial.unit === 'yards'
                      ? selectedMaterial.costPerYard || 0
                      : selectedMaterial.costPerUnit || 0
                  }
                  onChange={(e) => {
                    const cost = parseFloat(e.target.value) || 0;
                    if (selectedMaterial.unit === 'yards') {
                      setSelectedMaterial({ ...selectedMaterial, costPerYard: cost });
                    } else {
                      setSelectedMaterial({ ...selectedMaterial, costPerUnit: cost });
                    }
                  }}
                  InputProps={{
                    startAdornment: '$'
                  }}
                  disabled={selectedMaterial.isMaster}
                  helperText={selectedMaterial.isMaster ? "Cost varies by variant - set on individual variants" : ""}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Buy or Produce</InputLabel>
                  <Select
                    value={selectedMaterial.buyOrProduce}
                    label="Buy or Produce"
                    onChange={(e) =>
                      setSelectedMaterial({
                        ...selectedMaterial,
                        buyOrProduce: e.target.value as 'buy' | 'produce'
                      })
                    }
                  >
                    <MenuItem value="buy">Buy</MenuItem>
                    <MenuItem value="produce">Produce</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Supplier"
                  fullWidth
                  value={selectedMaterial.supplier || ''}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      supplier: e.target.value
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Alert severity="info" sx={{ width: '100%' }}>
                    Total Value: <strong>{formatCurrency((selectedMaterial.currentStock * ((selectedMaterial.unit === 'yards' ? selectedMaterial.costPerYard : selectedMaterial.costPerUnit) || 0)))}</strong>
                  </Alert>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  fullWidth
                  multiline
                  rows={3}
                  value={selectedMaterial.notes || ''}
                  onChange={(e) =>
                    setSelectedMaterial({
                      ...selectedMaterial,
                      notes: e.target.value
                    })
                  }
                />
              </Grid>
              {/* Bill of Materials for Produce Items */}
              {selectedMaterial.buyOrProduce === 'produce' && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Bill of Materials
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Define the materials required to produce ONE unit of this item.
                  </Alert>

                  {/* BOM Items Table */}
                  {selectedMaterial.billOfMaterials && selectedMaterial.billOfMaterials.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Material ID</TableCell>
                            <TableCell>Material Name</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell>Notes</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedMaterial.billOfMaterials.map((bomItem, index) => {
                            const material = materials.find(m => m.id === bomItem.materialId);
                            return (
                              <TableRow key={index}>
                                <TableCell>{bomItem.materialId}</TableCell>
                                <TableCell>{material?.name || 'Unknown'}</TableCell>
                                <TableCell align="right">
                                  {bomItem.quantity} {material?.unit || 'units'}
                                </TableCell>
                                <TableCell>{bomItem.notes || '-'}</TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      const updatedBOM = selectedMaterial.billOfMaterials!.filter((_, i) => i !== index);
                                      setSelectedMaterial({
                                        ...selectedMaterial,
                                        billOfMaterials: updatedBOM
                                      });
                                    }}
                                    title="Remove"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No bill of materials defined. Add materials below to enable production.
                    </Alert>
                  )}

                  {/* Add BOM Item Form */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Add Material to BOM
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Material</InputLabel>
                          <Select
                            value=""
                            label="Select Material"
                            onChange={(e) => {
                              const materialId = e.target.value;
                              const material = materials.find(m => m.id === materialId);
                              if (!material) return;

                              // Check if already in BOM
                              const existing = selectedMaterial.billOfMaterials?.find(
                                bom => bom.materialId === materialId
                              );
                              if (existing) {
                                setError(`${material.name} is already in the BOM`);
                                setTimeout(() => setError(null), 3000);
                                return;
                              }

                              // Add to BOM with quantity 1
                              const updatedBOM = [
                                ...(selectedMaterial.billOfMaterials || []),
                                {
                                  materialId: material.id,
                                  quantity: 1,
                                  notes: ''
                                }
                              ];
                              setSelectedMaterial({
                                ...selectedMaterial,
                                billOfMaterials: updatedBOM
                              });
                            }}
                          >
                            <MenuItem value="" disabled>
                              Choose a material...
                            </MenuItem>
                            {materials
                              .filter(m => m.active && m.id !== selectedMaterial.id)
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((material) => (
                                <MenuItem key={material.id} value={material.id}>
                                  {material.name} ({material.id}) - {material.buyOrProduce === 'buy' ? 'Buy' : 'Produce'}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <Typography variant="caption" color="text.secondary">
                          Select a material to add to this BOM. You can edit quantities after adding.
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Quick edit quantity for last added item */}
                    {selectedMaterial.billOfMaterials && selectedMaterial.billOfMaterials.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Quick Edit Last Added:
                        </Typography>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Quantity"
                              type="number"
                              size="small"
                              fullWidth
                              value={selectedMaterial.billOfMaterials[selectedMaterial.billOfMaterials.length - 1].quantity}
                              onChange={(e) => {
                                const updatedBOM = [...selectedMaterial.billOfMaterials!];
                                updatedBOM[updatedBOM.length - 1].quantity = parseFloat(e.target.value) || 0;
                                setSelectedMaterial({
                                  ...selectedMaterial,
                                  billOfMaterials: updatedBOM
                                });
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Notes (Optional)"
                              size="small"
                              fullWidth
                              value={selectedMaterial.billOfMaterials[selectedMaterial.billOfMaterials.length - 1].notes || ''}
                              onChange={(e) => {
                                const updatedBOM = [...selectedMaterial.billOfMaterials!];
                                updatedBOM[updatedBOM.length - 1].notes = e.target.value;
                                setSelectedMaterial({
                                  ...selectedMaterial,
                                  billOfMaterials: updatedBOM
                                });
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Advanced Options: Variant Management */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Variant Management
                </Typography>
              </Grid>

              {/* Create Color Variant - Only for masters or existing variants */}
              {selectedMaterial && (selectedMaterial.isMaster || selectedMaterial.parentMaterialId) && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => handleCreateVariant(selectedMaterial)}
                    fullWidth
                  >
                    Create Color Variant
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {selectedMaterial.isMaster
                      ? 'Create a new color variant of this master material.'
                      : 'Create a sibling variant with the same parent.'}
                  </Typography>
                </Grid>
              )}

              {/* Convert to Master - Only for standalone materials */}
              {selectedMaterial && !selectedMaterial.isMaster && !selectedMaterial.parentMaterialId && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => handleConvertToMaster(selectedMaterial)}
                    fullWidth
                  >
                    Convert to Master Material
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Convert this material to a master for grouping color variants. Current stock will be set to 0.
                  </Typography>
                </Grid>
              )}

              {/* Assign Parent - Only for standalone materials or variants */}
              {selectedMaterial && !selectedMaterial.isMaster && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={() => setAssignParentDialogOpen(true)}
                    fullWidth
                  >
                    {selectedMaterial.parentMaterialId ? 'Change Parent Material' : 'Assign to Parent Material'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {selectedMaterial.parentMaterialId
                      ? 'Change which master material this variant belongs to.'
                      : 'Link this material as a variant of a master material.'}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedMaterial.active}
                      onChange={(e) =>
                        setSelectedMaterial({
                          ...selectedMaterial,
                          active: e.target.checked
                        })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ flexGrow: 1 }}>
            {selectedMaterial && selectedMaterial.buyOrProduce === 'produce' && selectedMaterial.billOfMaterials && selectedMaterial.billOfMaterials.length > 0 && (
              <Button
                onClick={handleGenerateVariantsClick}
                variant="outlined"
                color="secondary"
                disabled={generatingVariants}
              >
                {generatingVariants ? 'Checking...' : 'Generate Variants'}
              </Button>
            )}
          </Box>
          {/* MADE-269: BOM colour suggestion banner */}
          {bomColorSuggestion && (
            <Box
              sx={{ mx: 2, mb: 2, p: 2, border: '1px solid', borderColor: 'info.light',
                    borderRadius: 1, backgroundColor: 'info.50' }}
              data-testid="bom-color-banner"
            >
              {bomColorSuggestion.suggestedColor && (
                <>
                  <Typography variant="body2" fontWeight={600}>
                    🎨 Colour auto-detected from BOM:
                    {bomColorSuggestion.currentColor && bomColorSuggestion.currentColor !== bomColorSuggestion.suggestedColor
                      ? ` ${bomColorSuggestion.suggestedColor} (current: ${bomColorSuggestion.currentColor})`
                      : ` ${bomColorSuggestion.suggestedColor}`
                    }
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button size="small" variant="contained" color="info"
                      data-testid="bom-color-apply-btn"
                      onClick={() => handleApplyBomColor(bomColorSuggestion.suggestedColor!)}>
                      Apply colour
                    </Button>
                    <Button size="small" variant="outlined"
                      data-testid="bom-color-dismiss-btn"
                      onClick={() => { setBomColorSuggestion(null); setEditDialogOpen(false); }}>
                      {bomColorSuggestion.currentColor ? `Keep ${bomColorSuggestion.currentColor}` : 'Dismiss'}
                    </Button>
                  </Box>
                </>
              )}
              {bomColorSuggestion.ambiguousColors && (
                <>
                  <Typography variant="body2" fontWeight={600}>
                    🎨 Multiple colours in BOM: {bomColorSuggestion.ambiguousColors.join(', ')}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Select
                      size="small"
                      value={bomColorSuggestionChoice}
                      onChange={e => setBomColorSuggestionChoice(e.target.value as string)}
                      data-testid="bom-color-choice-select"
                      sx={{ minWidth: 120 }}
                    >
                      {bomColorSuggestion.ambiguousColors.map(c => (
                        <MenuItem key={c} value={c}>{c}</MenuItem>
                      ))}
                    </Select>
                    <Button size="small" variant="contained" color="info"
                      data-testid="bom-color-apply-ambiguous-btn"
                      disabled={!bomColorSuggestionChoice}
                      onClick={() => handleApplyBomColor(bomColorSuggestionChoice)}>
                      Apply
                    </Button>
                    <Button size="small" variant="outlined"
                      onClick={() => { setBomColorSuggestion(null); setEditDialogOpen(false); }}>
                      Dismiss
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
          <Button onClick={() => { setBomColorSuggestion(null); setEditDialogOpen(false); }}>Cancel</Button>
          <Button onClick={handleSaveMaterial} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Material Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Inventory Item</DialogTitle>
        <DialogContent>
          {newMaterial && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Material ID"
                  fullWidth
                  required
                  value={newMaterial.id}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      id: e.target.value.toUpperCase()
                    })
                  }
                  helperText="Unique identifier (e.g., HW-BOLT-M4, PROD-BRACKET)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Name"
                  fullWidth
                  required
                  value={newMaterial.name}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      name: e.target.value
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newMaterial.type}
                    label="Type"
                    onChange={(e) =>
                      setNewMaterial({
                        ...newMaterial,
                        type: e.target.value as 'fabric' | 'hardware' | 'component' | 'filament' | 'material'
                      })
                    }
                  >
                    <MenuItem value="fabric">Fabric</MenuItem>
                    <MenuItem value="hardware">Hardware</MenuItem>
                    <MenuItem value="component">Component</MenuItem>
                    <MenuItem value="filament">Filament</MenuItem>
                    <MenuItem value="material">Material</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Unit</InputLabel>
                  <Select
                    value={newMaterial.unit}
                    label="Unit"
                    onChange={(e) =>
                      setNewMaterial({
                        ...newMaterial,
                        unit: e.target.value as 'yards' | 'pieces' | 'feet' | 'kg'
                      })
                    }
                  >
                    <MenuItem value="yards">Yards</MenuItem>
                    <MenuItem value="pieces">Pieces</MenuItem>
                    <MenuItem value="feet">Feet</MenuItem>
                    <MenuItem value="kg">Kilograms (kg)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Color (Optional)"
                  fullWidth
                  value={newMaterial.color || ''}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      color: e.target.value
                    })
                  }
                  disabled={newMaterial.isMaster}
                  helperText={newMaterial.isMaster ? "Master materials don't have colors" : ""}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newMaterial.isMaster || false}
                      onChange={(e) =>
                        setNewMaterial({
                          ...newMaterial,
                          isMaster: e.target.checked,
                          color: e.target.checked ? '' : newMaterial.color,
                          currentStock: e.target.checked ? 0 : newMaterial.currentStock
                        })
                      }
                    />
                  }
                  label="Master Material (for grouping color variants)"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Master materials group color variants and show rollup stock. They don't hold inventory directly.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={`Cost per ${newMaterial.unit === 'yards' ? 'Yard' : 'Unit'}`}
                  type="number"
                  fullWidth
                  value={
                    newMaterial.unit === 'yards'
                      ? newMaterial.costPerYard || 0
                      : newMaterial.costPerUnit || 0
                  }
                  onChange={(e) => {
                    const cost = parseFloat(e.target.value) || 0;
                    if (newMaterial.unit === 'yards') {
                      setNewMaterial({ ...newMaterial, costPerYard: cost });
                    } else {
                      setNewMaterial({ ...newMaterial, costPerUnit: cost });
                    }
                  }}
                  InputProps={{
                    startAdornment: '$'
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Current Stock"
                  type="number"
                  fullWidth
                  value={newMaterial.currentStock}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      currentStock: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: newMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: newMaterial.unit
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Minimum Stock Level"
                  type="number"
                  fullWidth
                  value={newMaterial.minStock}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      minStock: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: newMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: newMaterial.unit
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Reorder Quantity"
                  type="number"
                  fullWidth
                  value={newMaterial.reorderQuantity}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      reorderQuantity: parseFloat(e.target.value) || 0
                    })
                  }
                  inputProps={{
                    step: newMaterial.unit === 'kg' ? 0.001 : 1,
                    min: 0
                  }}
                  InputProps={{
                    endAdornment: newMaterial.unit
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Buy or Produce</InputLabel>
                  <Select
                    value={newMaterial.buyOrProduce}
                    label="Buy or Produce"
                    onChange={(e) =>
                      setNewMaterial({
                        ...newMaterial,
                        buyOrProduce: e.target.value as 'buy' | 'produce'
                      })
                    }
                  >
                    <MenuItem value="buy">Buy</MenuItem>
                    <MenuItem value="produce">Produce</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Supplier (Optional)"
                  fullWidth
                  value={newMaterial.supplier || ''}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      supplier: e.target.value
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes (Optional)"
                  fullWidth
                  multiline
                  rows={3}
                  value={newMaterial.notes || ''}
                  onChange={(e) =>
                    setNewMaterial({
                      ...newMaterial,
                      notes: e.target.value
                    })
                  }
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNewMaterial} variant="contained" color="success">
            Add Material
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inventory Report Dialog */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Inventory Financial Report</DialogTitle>
        <DialogContent>
          {inventoryReport && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
                    <Typography variant="h6" color="primary.contrastText">
                      Total Items
                    </Typography>
                    <Typography variant="h4" color="primary.contrastText" fontWeight="bold">
                      {inventoryReport.totalItems}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="h6" color="success.contrastText">
                      Total Value
                    </Typography>
                    <Typography variant="h4" color="success.contrastText" fontWeight="bold">
                      {formatCurrency(inventoryReport.totalValue)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {inventoryReport.lowStockItems.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Low Stock Items ({inventoryReport.lowStockItems.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell align="right">Current</TableCell>
                          <TableCell align="right">Min</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inventoryReport.lowStockItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">
                              {formatStock(item.currentStock)} {item.unit}
                            </TableCell>
                            <TableCell align="right">
                              {formatStock(item.minStock)} {item.unit}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {inventoryReport.outOfStockItems.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom color="error">
                    Out of Stock Items ({inventoryReport.outOfStockItems.length})
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Buy/Produce</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {inventoryReport.outOfStockItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.type}</TableCell>
                            <TableCell>
                              <Chip
                                label={item.buyOrProduce}
                                size="small"
                                color={item.buyOrProduce === 'buy' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open('/api/madebb/reports/financial', '_blank')} startIcon={<PrintIcon />}>
            Print Report
          </Button>
          <Button onClick={() => setReportDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Buy List Dialog */}
      <Dialog open={buyListDialogOpen} onClose={() => setBuyListDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Buy List</DialogTitle>
        <DialogContent>
          {buyList && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Total Estimated Cost: <strong>{formatCurrency(buyList.totalCost)}</strong>
              </Alert>

              {buyList.items.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Order Quantity</TableCell>
                        <TableCell align="right">Unit Cost</TableCell>
                        <TableCell align="right">Total Cost</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {buyList.items.map((item) => {
                        const unitCost = item.unit === 'yards' ? item.costPerYard : item.costPerUnit;
                        const totalCost = (unitCost || 0) * item.reorderQuantity;
                        return (
                          <TableRow key={item.id}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.supplier || 'N/A'}</TableCell>
                            <TableCell align="right">
                              {item.reorderQuantity} {item.unit}
                            </TableCell>
                            <TableCell align="right">{formatCurrency(unitCost || 0)}</TableCell>
                            <TableCell align="right">
                              <strong>{formatCurrency(totalCost)}</strong>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOrderClick(item)}
                              >
                                Order
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="success">No items need to be purchased at this time.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open('/api/madebb/reports/buy-list', '_blank')} startIcon={<PrintIcon />}>
            Print Buy List
          </Button>
          <Button onClick={() => setBuyListDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Order Material Dialog */}
      <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Order Material</DialogTitle>
        <DialogContent>
          {materialToOrder && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Ordering: <strong>{materialToOrder.name}</strong>
              </Alert>

              <TextField
                fullWidth
                label={`Quantity (${materialToOrder.unit})`}
                type="number"
                value={orderForm.quantity}
                onChange={(e) => setOrderForm({ ...orderForm, quantity: parseFloat(e.target.value) || 0 })}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: materialToOrder.unit === 'yards' ? 0.1 : 1 }}
              />

              <TextField
                fullWidth
                label="Cost Per Unit"
                type="number"
                value={orderForm.costPerUnit}
                onChange={(e) => setOrderForm({ ...orderForm, costPerUnit: parseFloat(e.target.value) || 0 })}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                }}
              />

              <TextField
                fullWidth
                label="Expected Delivery Date"
                type="date"
                value={orderForm.expectedDeliveryDate}
                onChange={(e) => setOrderForm({ ...orderForm, expectedDeliveryDate: e.target.value })}
                sx={{ mb: 2 }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="Supplier"
                value={orderForm.supplier}
                onChange={(e) => setOrderForm({ ...orderForm, supplier: e.target.value })}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={orderForm.notes}
                onChange={(e) => setOrderForm({ ...orderForm, notes: e.target.value })}
              />

              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Total Cost:</strong> {formatCurrency(orderForm.quantity * orderForm.costPerUnit)}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitOrder}
            disabled={!orderForm.quantity || !orderForm.costPerUnit || !orderForm.expectedDeliveryDate}
          >
            Place Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transit Inventory Dialog */}
      <Dialog open={transitInventoryDialogOpen} onClose={() => setTransitInventoryDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Inventory in Transit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {transitOrders.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Materials ordered from suppliers awaiting delivery
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>Material</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Cost Per Unit</TableCell>
                        <TableCell align="right">Total Cost</TableCell>
                        <TableCell>Expected Delivery</TableCell>
                        <TableCell>Ordered Date</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transitOrders.map((order) => {
                        const totalCost = order.quantityOrdered * order.costPerUnit;
                        const isOverdue = order.status === 'ordered' && new Date(order.expectedDeliveryDate) < new Date();

                        return (
                          <TableRow key={order.transitId}>
                            <TableCell>
                              <Chip
                                label={order.status === 'ordered' ? 'In Transit' : 'Received'}
                                color={order.status === 'ordered' ? (isOverdue ? 'error' : 'warning') : 'success'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {order.materialName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.materialType}
                              </Typography>
                            </TableCell>
                            <TableCell>{order.supplier || 'N/A'}</TableCell>
                            <TableCell align="right">
                              {order.quantityOrdered.toFixed(2)} {order.unit}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(order.costPerUnit)}
                              {order.finalCostPerUnit && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Final: {formatCurrency(order.finalCostPerUnit)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <strong>{formatCurrency(totalCost)}</strong>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color={isOverdue ? 'error' : 'inherit'}>
                                {new Date(order.expectedDeliveryDate).toLocaleDateString()}
                              </Typography>
                              {isOverdue && (
                                <Typography variant="caption" color="error">
                                  Overdue
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {new Date(order.orderedDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="center">
                              {order.status === 'ordered' ? (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleReceiveClick(order)}
                                >
                                  Receive
                                </Button>
                              ) : (
                                <Chip
                                  label={`Received ${new Date(order.receivedDate!).toLocaleDateString()}`}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Alert severity="info">No materials in transit at this time.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open('/api/madebb/reports/transit', '_blank')} startIcon={<PrintIcon />}>
            Print Transit Report
          </Button>
          <Button onClick={() => setTransitInventoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Receive Material Dialog */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Receive Material</DialogTitle>
        <DialogContent>
          {orderToReceive && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Receiving: <strong>{orderToReceive.materialName}</strong>
              </Alert>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Quantity Ordered
                  </Typography>
                  <Typography variant="h6">
                    {orderToReceive.quantityOrdered.toFixed(2)} {orderToReceive.unit}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Original Cost Per Unit
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(orderToReceive.costPerUnit)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal (Material Cost)
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(orderToReceive.quantityOrdered * orderToReceive.costPerUnit)}
                  </Typography>
                </Grid>
              </Grid>

              <TextField
                fullWidth
                label="Additional Fees (shipping, duties, etc.)"
                type="number"
                value={receiveForm.additionalFees}
                onChange={(e) => setReceiveForm({ ...receiveForm, additionalFees: parseFloat(e.target.value) || 0 })}
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                }}
                helperText="Any additional costs incurred for this order"
              />

              <TextField
                fullWidth
                label="Receiving Notes"
                multiline
                rows={3}
                value={receiveForm.notes}
                onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })}
                sx={{ mb: 3 }}
                placeholder="Any notes about the delivery, condition, etc."
              />

              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Final Cost Calculation
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">Material Cost:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {formatCurrency(orderToReceive.quantityOrdered * orderToReceive.costPerUnit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Additional Fees:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {formatCurrency(receiveForm.additionalFees)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">Total Cost:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {formatCurrency(
                        orderToReceive.quantityOrdered * orderToReceive.costPerUnit + receiveForm.additionalFees
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <hr style={{ margin: '8px 0', border: 'none', borderTop: '1px solid rgba(0,0,0,0.12)' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2">
                      <strong>Final Cost Per Unit:</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" align="right">
                      <strong>
                        {formatCurrency(
                          (orderToReceive.quantityOrdered * orderToReceive.costPerUnit + receiveForm.additionalFees) /
                            orderToReceive.quantityOrdered
                        )}
                      </strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleSubmitReceive}>
            Confirm Receipt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Color Variant Dialog */}
      <Dialog
        open={createVariantDialogOpen}
        onClose={() => setCreateVariantDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Color Variant</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Creating a color variant of: <strong>{selectedMaterial?.name}</strong>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Variant Color/Value"
                  fullWidth
                  required
                  value={variantForm.variantValue}
                  onChange={(e) => setVariantForm({ ...variantForm, variantValue: e.target.value })}
                  helperText="e.g., 'Navy Blue', 'Red', 'Silver'"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Variant ID"
                  fullWidth
                  required
                  value={variantForm.id}
                  onChange={(e) => setVariantForm({ ...variantForm, id: e.target.value.toUpperCase() })}
                  helperText="Unique identifier for this variant"
                />
              </Grid>
            </Grid>

            {bomUsageItems.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  BOM Replication Options
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This material is used in {bomUsageItems.length} item(s). Select items to create color variants for:
                </Alert>

                <Paper variant="outlined" sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
                  {bomUsageItems.map((item) => (
                    <FormControlLabel
                      key={item.id}
                      control={
                        <Checkbox
                          checked={selectedItemsToReplicate.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItemsToReplicate([...selectedItemsToReplicate, item.id]);
                            } else {
                              setSelectedItemsToReplicate(
                                selectedItemsToReplicate.filter((id) => id !== item.id)
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.id} ({item.type})
                          </Typography>
                        </Box>
                      }
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                </Paper>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Selected items will be cloned with their BOM updated to use the new variant.
                </Typography>
              </Box>
            )}

            {bomUsageItems.length === 0 && (
              <Alert severity="info" sx={{ mt: 3 }}>
                This material is not currently used in any other items' BOMs.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateVariantDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSubmitVariant}
            variant="contained"
            color="primary"
            disabled={!variantForm.variantValue || !variantForm.id}
          >
            Create Variant
            {selectedItemsToReplicate.length > 0 && ` & Replicate ${selectedItemsToReplicate.length} Item${selectedItemsToReplicate.length > 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Parent Dialog */}
      <Dialog
        open={assignParentDialogOpen}
        onClose={() => {
          setAssignParentDialogOpen(false);
          setSelectedParentId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedMaterial?.parentMaterialId ? 'Change Parent Material' : 'Assign to Parent Material'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedMaterial?.parentMaterialId && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Current parent: <strong>{materials.find(m => m.id === selectedMaterial.parentMaterialId)?.name || 'Unknown'}</strong>
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a master material to link "{selectedMaterial?.name}" as a color variant.
            </Typography>

            <FormControl fullWidth required>
              <InputLabel>Parent Material</InputLabel>
              <Select
                value={selectedParentId}
                label="Parent Material"
                onChange={(e) => setSelectedParentId(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Choose a master material...
                </MenuItem>
                {materials
                  .filter(m => m.isMaster && m.id !== selectedMaterial?.id)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((material) => (
                    <MenuItem key={material.id} value={material.id}>
                      {material.name} ({material.id})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                This will:
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Link this material as a variant of the selected master</li>
                <li>Update the material name to include the color/variant value</li>
                <li>Set variant type to "color"</li>
              </ul>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssignParentDialogOpen(false);
            setSelectedParentId('');
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignParent}
            variant="contained"
            color="primary"
            disabled={!selectedParentId}
          >
            Assign Parent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Material</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{materialToDelete?.name}</strong>?
          </Typography>
          {materialToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Item Code:</strong> {materialToDelete.id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Stock:</strong> {formatStock(materialToDelete.currentStock)} {materialToDelete.unit}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Type:</strong> {materialToDelete.type}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Variants Confirmation Dialog */}
      <Dialog
        open={generateVariantsDialogOpen}
        onClose={() => !generatingVariants && setGenerateVariantsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generate Variants</DialogTitle>
        <DialogContent>
          {variantPreview && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{variantPreview.variantCount}</strong> variant{variantPreview.variantCount !== 1 ? 's' : ''} will be created based on the bill of materials.
              </Alert>

              <Typography variant="h6" gutterBottom>
                Variants to Create:
              </Typography>

              <Box sx={{ maxHeight: '400px', overflowY: 'auto', bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                {variantPreview.preview && variantPreview.preview.slice(0, 10).map((variant: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      mb: 1,
                      p: 1.5,
                      bgcolor: 'white',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.300'
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      {variant.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Materials: {variant.materialCombination.map((mc: any) => `${mc.master} - ${mc.variant}`).join(', ')}
                    </Typography>
                  </Box>
                ))}
                {variantPreview.preview && variantPreview.preview.length > 10 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    ...and {variantPreview.preview.length - 10} more
                  </Typography>
                )}
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                This action will create new inventory items with updated BOMs. Each variant will start with 0 stock.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateVariantsDialogOpen(false)} disabled={generatingVariants}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmGenerateVariants}
            variant="contained"
            color="primary"
            disabled={generatingVariants}
          >
            {generatingVariants ? 'Creating...' : 'Create Variants'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-Cascade Confirmation Dialog — MADE-270: dry-run preview + checkboxes */}
      <Dialog
        open={cascadeDialogOpen}
        onClose={() => !cascading && setCascadeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Push Colour to Products</DialogTitle>
        <DialogContent>
          {cascadePreview && (() => {
            // Support both old preview[] format and new would_create[] format
            const toCreate: any[] = cascadePreview.would_create ?? cascadePreview.preview ?? [];
            const skipped: any[] = cascadePreview.skipped ?? [];
            const selectedCount = [...selectedCascadeIds].filter(id => toCreate.some((item: any) => (item.masterId ?? item.product_id) === id)).length;
            return (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>{toCreate.length} variant{toCreate.length !== 1 ? 's' : ''}</strong> can be created.
                {skipped.length > 0 && ` ${skipped.length} product${skipped.length !== 1 ? 's' : ''} will be skipped.`}
                {' '}New variants will have <strong>shop_visible = false</strong> — you control when they go live.
              </Alert>

              <Typography variant="h6" gutterBottom>
                Will Create ({selectedCount} selected):
              </Typography>

              <Box sx={{ maxHeight: '300px', overflowY: 'auto', bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                {toCreate.map((item: any, index: number) => {
                  const itemId = item.masterId ?? item.product_id ?? String(index);
                  const isChecked = selectedCascadeIds.has(itemId);
                  return (
                    <Box
                      key={index}
                      sx={{ mb: 1, p: 1.5, bgcolor: 'white', borderRadius: 1,
                            border: '1px solid', borderColor: 'grey.300',
                            display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        onChange={e => {
                          setSelectedCascadeIds(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(itemId); else next.delete(itemId);
                            return next;
                          });
                        }}
                        data-testid={`cascade-checkbox-${index}`}
                      />
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {item.new_variant_name ?? item.newVariantName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Product: {item.product_name ?? item.masterName}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
                {toCreate.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No new variants to create.</Typography>
                )}
              </Box>

              {skipped.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Skipped ({skipped.length}):
                  </Typography>
                  <Box sx={{ maxHeight: '200px', overflowY: 'auto', bgcolor: 'grey.50', p: 1, borderRadius: 1, mb: 2 }}>
                    {skipped.map((item: any, index: number) => (
                      <Box key={index} sx={{ mb: 0.5, p: 1, opacity: 0.7 }}>
                        <Typography variant="body2">
                          ⏭️ {item.product_name ?? item.masterName} — {item.skip_reason}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCascadeDialogOpen(false);
              setCascadePreview(null);
              setNewlyCreatedVariantId(null);
              setSelectedCascadeIds(new Set());
              loadMaterials();
            }}
            disabled={cascading}
          >
            Skip / Close
          </Button>
          <Button
            onClick={handleConfirmCascade}
            variant="contained"
            color="primary"
            disabled={cascading}
          >
            {cascading ? 'Creating...' : `Push ${selectedCascadeIds.size} variant${selectedCascadeIds.size !== 1 ? 's' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
